export interface TrayPanelAnchor {
  displayId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TrayPanelPosition {
  left: number;
  top: number;
  pointerOffset: number;
  pointerEdge: "top" | "bottom" | "left" | "right";
}

export function advanceTrayPanelPressGate(
  readyForNewPress: boolean,
  primaryDown: boolean,
): { readyForNewPress: boolean; shouldCheckOutside: boolean } {
  if (!primaryDown) {
    return { readyForNewPress: true, shouldCheckOutside: false };
  }
  return {
    readyForNewPress: false,
    shouldCheckOutside: readyForNewPress,
  };
}

export function shouldCloseTrayPanelForPress(input: {
  panelDisplayId: number | null;
  pressDisplayId?: number;
  pressX: number;
  pressY: number;
  windowOriginX: number;
  windowOriginY: number;
  panelRect: Pick<DOMRect, "left" | "right" | "top" | "bottom">;
}): boolean {
  if (input.pressDisplayId !== input.panelDisplayId) return true;
  const x = input.pressX - input.windowOriginX;
  const y = input.pressY - input.windowOriginY;
  return (
    x < input.panelRect.left ||
    x > input.panelRect.right ||
    y < input.panelRect.top ||
    y > input.panelRect.bottom
  );
}

export function trayPopoverContainsPress(input: {
  panelDisplayId: number | null;
  pressDisplayId?: number;
  x: number;
  y: number;
  popovers: Array<Pick<DOMRect, "left" | "right" | "top" | "bottom">>;
}): boolean {
  if (input.panelDisplayId !== input.pressDisplayId) return false;
  return input.popovers.some(
    (bounds) =>
      input.x >= bounds.left &&
      input.x <= bounds.right &&
      input.y >= bounds.top &&
      input.y <= bounds.bottom,
  );
}

export function trayAnchorContainsPress(
  anchor: TrayPanelAnchor,
  pressDisplayId: number | undefined,
  x: number,
  y: number,
): boolean {
  if (pressDisplayId !== anchor.displayId) return false;
  const tolerance = 4;
  return (
    x >= anchor.x - tolerance &&
    x <= anchor.x + anchor.width + tolerance &&
    y >= anchor.y - tolerance &&
    y <= anchor.y + anchor.height + tolerance
  );
}

const GAP = 8;
// The native tray rect includes a little space below the visible menu bar.
// Tuck the panel under that inset so both manual and auto-opened trays sit close.
const TOP_GAP = -4;
const EDGE = 8;
// The overlay's top edge is the menu bar's bottom edge, so a top-anchored
// panel hangs just below the menu bar instead of keeping the side margin.
const MENU_BAR_GAP = 4;
const POINTER_INSET = 22;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(Math.max(value, minimum), maximum);

/** Place the panel toward the display interior for top, bottom, and vertical
 * taskbars, then clamp it to the current overlay's work area. */
export function positionTrayPanel(
  anchor: TrayPanelAnchor,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
): TrayPanelPosition {
  const centerX = anchor.x + anchor.width / 2;
  const centerY = anchor.y + anchor.height / 2;
  const verticalRail =
    centerY > viewport.height * 0.2 && centerY < viewport.height * 0.8;
  const leftRail = verticalRail && centerX < viewport.width * 0.2;
  const rightRail = verticalRail && centerX > viewport.width * 0.8;

  let left = centerX - panel.width / 2;
  let top =
    centerY < viewport.height / 2
      ? anchor.y + anchor.height + TOP_GAP
      : anchor.y - panel.height - GAP;
  let pointerEdge: TrayPanelPosition["pointerEdge"] =
    centerY < viewport.height / 2 ? "top" : "bottom";

  if (leftRail) {
    left = anchor.x + anchor.width + GAP;
    top = centerY - panel.height / 2;
    pointerEdge = "left";
  } else if (rightRail) {
    left = anchor.x - panel.width - GAP;
    top = centerY - panel.height / 2;
    pointerEdge = "right";
  }

  left = clamp(left, EDGE, Math.max(EDGE, viewport.width - panel.width - EDGE));
  const minTop = pointerEdge === "top" ? MENU_BAR_GAP : EDGE;
  top = clamp(
    top,
    minTop,
    Math.max(minTop, viewport.height - panel.height - EDGE),
  );
  return {
    left,
    top,
    pointerOffset:
      pointerEdge === "left" || pointerEdge === "right"
        ? clamp(centerY - top, POINTER_INSET, panel.height - POINTER_INSET)
        : clamp(centerX - left, POINTER_INSET, panel.width - POINTER_INSET),
    pointerEdge,
  };
}

/** A rectangle in one display's overlay coordinates (CSS px from the work-area origin). */
interface OverlayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The tray panel's rect, with the edge that faces the tray icon. */
export interface TrayPanelRect extends OverlayRect {
  pointerEdge: TrayPanelPosition["pointerEdge"];
}

/** The room the pocket may fill, and the edge of that room it hangs from. */
export interface SettingsPocketPlacement {
  left: number;
  top: number;
  /** The pocket's height follows its content up to this. */
  maxHeight: number;
  pinnedEdge: "top" | "bottom";
}

export const SETTINGS_POCKET_WIDTH = 390;
const POCKET_GAP = 12;

let trayPanelSize = { width: 390, height: 420 };

/** The full tray records its measured size so Settings can place itself as if the tray were open. */
export function rememberTrayPanelSize(size: { width: number; height: number }) {
  if (
    Number.isFinite(size.width) &&
    Number.isFinite(size.height) &&
    size.width > 0 &&
    size.height > 0
  )
    trayPanelSize = { width: size.width, height: size.height };
}

export function lastTrayPanelSize(): { width: number; height: number } {
  return { ...trayPanelSize };
}

/** Where the tray panel sits, or would sit, for this anchor. */
export function trayPanelRect(
  anchor: TrayPanelAnchor,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): TrayPanelRect {
  const position = positionTrayPanel(anchor, size, viewport);
  return {
    left: position.left,
    top: position.top,
    width: size.width,
    height: size.height,
    pointerEdge: position.pointerEdge,
  };
}

/** The same spot on another display: the distance to the nearer vertical and horizontal edges stays. */
export function translateOverlayRect<Rect extends OverlayRect>(
  rect: Rect,
  from: { width: number; height: number },
  to: { width: number; height: number },
): Rect {
  const nearRight = rect.left + rect.width / 2 > from.width / 2;
  const nearBottom = rect.top + rect.height / 2 > from.height / 2;
  return {
    ...rect,
    left: nearRight ? to.width - (from.width - rect.left) : rect.left,
    top: nearBottom ? to.height - (from.height - rect.top) : rect.top,
  };
}

/** How tall a pocket hanging from `top` may grow before the work area's edge margin. */
export function settingsPocketMaxHeight(
  top: number,
  viewportHeight: number,
): number {
  return Math.max(0, viewportHeight - top - EDGE);
}

/**
 * Settings sits left of the tray panel with a 12px gap; on the tray's right
 * when the left has no room. Vertically it mirrors the tray: beside a tray
 * that opens upward from a bottom taskbar it shares the tray's bottom edge
 * and grows upward; otherwise it shares the tray's top edge and grows
 * downward. Either way it may grow to the work area's 8px margin (spec §4).
 */
export function positionSettingsPocket(
  tray: TrayPanelRect,
  viewport: { width: number; height: number },
): SettingsPocketPlacement {
  const width = SETTINGS_POCKET_WIDTH;
  const leftOfTray = tray.left - POCKET_GAP - width;
  const left =
    leftOfTray >= EDGE
      ? Math.min(leftOfTray, Math.max(EDGE, viewport.width - width - EDGE))
      : clamp(
          tray.left + tray.width + POCKET_GAP,
          EDGE,
          Math.max(EDGE, viewport.width - width - EDGE),
        );
  if (tray.pointerEdge === "bottom") {
    const bottom = clamp(
      tray.top + tray.height,
      EDGE,
      Math.max(EDGE, viewport.height - EDGE),
    );
    return { left, top: EDGE, maxHeight: bottom - EDGE, pinnedEdge: "bottom" };
  }
  const top = clamp(tray.top, 0, Math.max(0, viewport.height - EDGE));
  return {
    left,
    top,
    maxHeight: settingsPocketMaxHeight(top, viewport.height),
    pinnedEdge: "top",
  };
}
